import React, { useMemo, useState } from "react";
import {
  getMerchantAppBranding,
  MerchantPlatform,
} from "./merchantBranding";
import {
  articlesForPlatform,
  HELP_CATEGORIES,
  HelpArticle,
  HelpCategoryId,
  supportMailto,
} from "./merchantHelpContent";
import "./MerchantHelpCenter.css";

interface MerchantHelpCenterProps {
  platform: MerchantPlatform;
  storeName?: string;
  onReplayGetStarted?: () => void;
}

const MerchantHelpCenter: React.FC<MerchantHelpCenterProps> = ({
  platform,
  storeName,
  onReplayGetStarted,
}) => {
  const branding = getMerchantAppBranding(platform);
  const articles = useMemo(() => articlesForPlatform(platform), [platform]);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<HelpCategoryId | "all">("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const categories = useMemo(() => {
    const used = new Set(articles.map((item) => item.category));
    return HELP_CATEGORIES.filter((category) => used.has(category.id));
  }, [articles]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return articles.filter((article) => {
      if (categoryId !== "all" && article.category !== categoryId) {
        return false;
      }
      if (!needle) {
        return true;
      }
      const haystack = [
        article.title,
        article.summary,
        ...(article.tags || []),
        ...(article.steps || []),
        ...(article.body || []),
        ...(article.tips || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [articles, categoryId, query]);

  const featured = useMemo(() => {
    const preferred =
      platform === "shopify"
        ? ["shopify-install", "shopify-checkout-rates", "view-orders", "print-labels"]
        : platform === "custom"
        ? ["custom-add-store", "custom-api-key", "custom-rates-api", "view-orders"]
        : ["portal-overview", "view-orders", "print-labels", "settings-profile"];
    return preferred
      .map((id) => articles.find((article) => article.id === id))
      .filter(Boolean) as HelpArticle[];
  }, [articles, platform]);

  const openArticle = (id: string) => {
    setOpenId((current) => (current === id ? null : id));
    window.requestAnimationFrame(() => {
      const el = document.getElementById(`help-article-${id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  return (
    <section className="zulu-plugin__stage-body is-page zulu-help">
      <div className="zulu-help__hero">
        <div>
          <p className="zulu-help__eyebrow">{branding.label} help center</p>
          <h2>How can we help{storeName ? ` with ${storeName}` : ""}?</h2>
          <p>
            Step-by-step guides for your {branding.label.toLowerCase()} store —
            orders, labels, settings, and integration setup.
          </p>
        </div>
        <div className="zulu-help__search">
          <label className="visually-hidden" htmlFor="merchant-help-search">
            Search help
          </label>
          <span className="zulu-help__search-icon" aria-hidden="true">
            <IconSearch />
          </span>
          <input
            id="merchant-help-search"
            type="search"
            placeholder="Search guides (rates, labels, API key…)"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {!query && categoryId === "all" ? (
        <div className="zulu-help__featured">
          <h3>Popular for {branding.label}</h3>
          <div className="zulu-help__featured-grid">
            {featured.map((article) => (
              <button
                key={article.id}
                type="button"
                className="zulu-help__featured-card"
                onClick={() => {
                  setCategoryId(article.category);
                  openArticle(article.id);
                }}
              >
                <strong>{article.title}</strong>
                <span>{article.summary}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="zulu-help__layout">
        <aside className="zulu-help__cats" aria-label="Help categories">
          <button
            type="button"
            className={categoryId === "all" ? "is-active" : ""}
            onClick={() => setCategoryId("all")}
          >
            All topics
            <span>{articles.length}</span>
          </button>
          {categories.map((category) => {
            const count = articles.filter(
              (article) => article.category === category.id
            ).length;
            return (
              <button
                key={category.id}
                type="button"
                className={categoryId === category.id ? "is-active" : ""}
                onClick={() => setCategoryId(category.id)}
              >
                <span className="zulu-help__cat-main">
                  <CategoryIcon name={category.icon} />
                  {category.title}
                </span>
                <span>{count}</span>
              </button>
            );
          })}
        </aside>

        <div className="zulu-help__list" role="list">
          {filtered.length === 0 ? (
            <div className="zulu-help__empty">
              <h3>No matching guides</h3>
              <p>Try another search term or clear filters.</p>
              <button
                type="button"
                className="zulu-help__ghost-btn"
                onClick={() => {
                  setQuery("");
                  setCategoryId("all");
                }}
              >
                Clear search
              </button>
            </div>
          ) : (
            filtered.map((article) => {
              const isOpen = openId === article.id;
              return (
                <article
                  key={article.id}
                  id={`help-article-${article.id}`}
                  className={`zulu-help__article${isOpen ? " is-open" : ""}`}
                  role="listitem"
                >
                  <button
                    type="button"
                    className="zulu-help__article-toggle"
                    aria-expanded={isOpen}
                    onClick={() => openArticle(article.id)}
                  >
                    <span>
                      <strong>{article.title}</strong>
                      <span className="zulu-help__article-summary">
                        {article.summary}
                      </span>
                    </span>
                    <span className="zulu-help__chevron" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="zulu-help__article-body">
                      {article.body?.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {article.steps && article.steps.length > 0 ? (
                        <ol className="zulu-help__steps">
                          {article.steps.map((step, index) => (
                            <li key={`${article.id}-step-${index}`}>
                              <span className="zulu-help__step-num">
                                {index + 1}
                              </span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      ) : null}
                      {article.tips && article.tips.length > 0 ? (
                        <div className="zulu-help__tips">
                          <strong>Tips</strong>
                          <ul>
                            {article.tips.map((tip) => (
                              <li key={tip}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {article.tags.length > 0 ? (
                        <div className="zulu-help__tags">
                          {article.tags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })
          )}
        </div>
      </div>

      {onReplayGetStarted ? (
        <div className="zulu-help__support zulu-help__support--tips">
          <div>
            <h3>Get started tips</h3>
            <p>
              Replay the first-visit tour covering Dashboard, Orders,
              Notifications, Settings, and {branding.label} integration basics.
            </p>
          </div>
          <button
            type="button"
            className="zulu-help__support-btn is-secondary"
            onClick={onReplayGetStarted}
          >
            Replay tips
          </button>
        </div>
      ) : null}

      <div className="zulu-help__support">
        <div>
          <h3>Still need help?</h3>
          <p>
            Email Zulu support with your store name
            {storeName ? ` (${storeName})` : ""} and platform order IDs. We
            usually reply within one business day.
          </p>
        </div>
        <a
          className="zulu-help__support-btn"
          href={supportMailto(branding.pluginLabel)}
        >
          Email support
        </a>
      </div>
    </section>
  );
};

const CategoryIcon: React.FC<{ name: string }> = ({ name }) => {
  switch (name) {
    case "rocket":
      return <IconRocket />;
    case "orders":
      return <IconOrders />;
    case "label":
      return <IconLabel />;
    case "bell":
      return <IconBell />;
    case "gear":
      return <IconGear />;
    case "plug":
      return <IconPlug />;
    default:
      return <IconWrench />;
  }
};

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l5 5" />
  </svg>
);

const IconRocket = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 3c3 2 5 5 6 9-4 1-7 3-9 6-1-3-3-5-6-6 4-1 7-4 9-9z" />
    <path d="M8 16l-3 5 5-3" />
  </svg>
);

const IconOrders = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 7h16M4 12h16M4 17h10" />
  </svg>
);

const IconLabel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 7h9l7 7-7 7H4V7z" />
    <circle cx="9" cy="12" r="1.2" />
  </svg>
);

const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M6 17h12l-1-7a5 5 0 10-10 0l-1 7z" />
    <path d="M10 17a2 2 0 004 0" />
  </svg>
);

const IconGear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M3 12h2M19 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const IconPlug = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M9 7V3M15 7V3M8 7h8v4a4 4 0 01-4 4h0a4 4 0 01-4-4V7z" />
    <path d="M12 15v6" />
  </svg>
);

const IconWrench = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14.5 6.5a4 4 0 00-5.7 5.7L4 17l3 3 4.8-4.8a4 4 0 005.7-5.7L15 12l-2.5-2.5 2-3z" />
  </svg>
);

export default MerchantHelpCenter;
